import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Phone, MapPin, ArrowRight, CheckCircle, Star, Users, Award, Shield, Heart, ChevronDown, Mail, Sparkles, MessageSquare } from 'lucide-react';
import { OptimizedImage } from '../../components/OptimizedImage';

export const MedicalSpa: React.FC = () => {
  const [activePage, setActivePage] = useState<string>('home');
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);
  const [activeGalleryTab, setActiveGalleryTab] = useState<string>('facial');

  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const renderNavigation = () => {
    return (
      <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/templates/medical-spa" onClick={() => setActivePage('home')} className="flex-shrink-0 flex items-center">
                <Sparkles className="h-8 w-8 text-purple-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">Radiance Medical Spa</span>
              </Link>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => setActivePage('about')}
                className={`font-medium ${activePage === 'about' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                About
              </button>
              <button 
                onClick={() => setActivePage('treatments')}
                className={`font-medium ${activePage === 'treatments' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Treatments
              </button>
              <button 
                onClick={() => setActivePage('gallery')}
                className={`font-medium ${activePage === 'gallery' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Gallery
              </button>
              <button 
                onClick={() => setActivePage('membership')}
                className={`font-medium ${activePage === 'membership' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Membership
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className={`font-medium ${activePage === 'contact' ? 'text-purple-600' : 'text-gray-500 hover:text-gray-900'}`}
              >
                Contact
              </button>
            </div>
            
            <div className="hidden md:flex items-center">
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Book Appointment
              </button>
            </div>
            
            <div className="md:hidden flex items-center">
              <button className="text-gray-500 hover:text-gray-900">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>
    );
  };

  const renderPage = () => {
    switch (activePage) {
      case 'about':
        return renderAboutPage();
      case 'treatments':
        return renderTreatmentsPage();
      case 'gallery':
        return renderGalleryPage();
      case 'membership':
        return renderMembershipPage();
      case 'contact':
        return renderContactPage();
      case 'booking':
        return renderBookingPage();
      default:
        return renderHomePage();
    }
  };

  const renderHomePage = () => {
    const treatments = [
      {
        title: "Facial Treatments",
        description: "Rejuvenate your skin with our customized facial treatments",
        image: "https://images.pexels.com/photos/3997379/pexels-photo-3997379.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
      },
      {
        title: "Body Contouring",
        description: "Sculpt your body with non-invasive contouring treatments",
        image: "https://images.pexels.com/photos/7446144/pexels-photo-7446144.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
      },
      {
        title: "Laser Treatments",
        description: "Advanced laser technology for skin rejuvenation and hair removal",
        image: "https://images.pexels.com/photos/7446989/pexels-photo-7446989.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
      },
      {
        title: "Injectables",
        description: "Botox and dermal fillers for a more youthful appearance",
        image: "https://images.pexels.com/photos/7446997/pexels-photo-7446997.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
      }
    ];

    const testimonials = [
      {
        name: "Jennifer L.",
        text: "I've been coming to Radiance for over a year now and the results have been amazing. The staff is professional and the atmosphere is so relaxing.",
        treatment: "Facial Treatments"
      },
      {
        name: "Michael R.",
        text: "The body contouring treatments have helped me achieve results that diet and exercise alone couldn't. I'm so much more confident now!",
        treatment: "Body Contouring"
      },
      {
        name: "Sarah T.",
        text: "The membership program is such a great value. I love being able to maintain my treatments regularly without breaking the bank.",
        treatment: "Membership"
      }
    ];

    return (
      <div>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-6">Discover Your Natural Radiance</h1>
                <p className="text-xl text-white/90 mb-8 leading-relaxed">
                  Experience the perfect blend of luxury, science, and self-care at Radiance Medical Spa. 
                  Our advanced treatments and expert practitioners will help you look and feel your best.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => setActivePage('booking')}
                    className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                  >
                    Book a Consultation
                  </button>
                  <button 
                    onClick={() => setActivePage('treatments')}
                    className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
                  >
                    Explore Treatments
                  </button>
                </div>
              </div>
              <div>
                <OptimizedImage
                  src="https://images.pexels.com/photos/3997981/pexels-photo-3997981.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                  alt="Luxury spa treatment"
                  width={800}
                  height={600}
                  className="rounded-lg shadow-xl"
                  priority={true}
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Info */}
        <section className="py-8 bg-white shadow-md relative -mt-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-purple-600 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-900">Spa Hours</h3>
                  <p className="text-gray-600">Mon-Fri: 9am-7pm</p>
                  <p className="text-gray-600">Sat: 9am-5pm</p>
                </div>
              </div>
              <div className="flex items-center">
                <Phone className="h-8 w-8 text-purple-600 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-900">Contact Us</h3>
                  <p className="text-gray-600">(555) 987-6543</p>
                  <p className="text-gray-600">info@radiancespa.com</p>
                </div>
              </div>
              <div className="flex items-center">
                <MapPin className="h-8 w-8 text-purple-600 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-900">Location</h3>
                  <p className="text-gray-600">123 Wellness Avenue</p>
                  <p className="text-gray-600">Anytown, ST 12345</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Treatments */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Treatments</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover our most popular treatments designed to enhance your natural beauty.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {treatments.map((treatment, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden group">
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={treatment.image} 
                      alt={treatment.title} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{treatment.title}</h3>
                    <p className="text-gray-600 mb-4">{treatment.description}</p>
                    <button 
                      onClick={() => setActivePage('treatments')}
                      className="text-purple-600 font-medium hover:text-purple-700 transition-colors flex items-center"
                    >
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <button 
                onClick={() => setActivePage('treatments')}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center"
              >
                View All Treatments
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </section>

        {/* About Us Preview */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://images.pexels.com/photos/3997378/pexels-photo-3997378.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop" 
                  alt="Spa interior" 
                  className="rounded-lg shadow-lg"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Radiance Medical Spa</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  At Radiance Medical Spa, we combine luxury with medical expertise to provide you with the highest 
                  quality aesthetic treatments. Our state-of-the-art facility is designed to create a serene 
                  environment where you can relax and rejuvenate.
                </p>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Our team of licensed medical professionals and aestheticians are passionate about helping you 
                  achieve your beauty and wellness goals. We use only the most advanced technologies and 
                  premium products to ensure exceptional results.
                </p>
                <button 
                  onClick={() => setActivePage('about')}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center"
                >
                  Learn More About Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Client Testimonials</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Hear what our clients have to say about their experiences at Radiance Medical Spa.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-purple-600">{testimonial.treatment}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership Preview */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Radiance Membership Program</h2>
              <p className="text-xl text-white/90 max-w-3xl mx-auto">
                Join our membership program and enjoy exclusive benefits, discounts, and priority booking.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-4">Silver</h3>
                <div className="text-3xl font-bold mb-4">$99<span className="text-lg font-normal">/month</span></div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>One signature treatment monthly</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>10% off additional services</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>5% off retail products</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>Member-only events</span>
                  </li>
                </ul>
                <button 
                  onClick={() => setActivePage('membership')}
                  className="w-full bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Learn More
                </button>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-lg transform scale-105 shadow-lg">
                <div className="absolute top-0 right-0 bg-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-lg">
                  MOST POPULAR
                </div>
                <h3 className="text-2xl font-bold mb-4">Gold</h3>
                <div className="text-3xl font-bold mb-4">$179<span className="text-lg font-normal">/month</span></div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>Two signature treatments monthly</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>15% off additional services</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>10% off retail products</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>Priority booking</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>Complimentary birthday treatment</span>
                  </li>
                </ul>
                <button 
                  onClick={() => setActivePage('membership')}
                  className="w-full bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Learn More
                </button>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-lg">
                <h3 className="text-2xl font-bold mb-4">Platinum</h3>
                <div className="text-3xl font-bold mb-4">$299<span className="text-lg font-normal">/month</span></div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>Four signature treatments monthly</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>20% off additional services</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>15% off retail products</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>VIP priority booking</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-pink-300 mr-3 flex-shrink-0" />
                    <span>Complimentary guest pass quarterly</span>
                  </li>
                </ul>
                <button 
                  onClick={() => setActivePage('membership')}
                  className="w-full bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Ready to Experience the Radiance Difference?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Book your consultation today and take the first step toward looking and feeling your best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Book a Consultation
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className="bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <Sparkles className="h-8 w-8 text-pink-400" />
                  <span className="ml-2 text-xl font-bold">Radiance Medical Spa</span>
                </div>
                <p className="text-gray-400 mb-4">
                  Luxury medical spa offering advanced aesthetic treatments in a serene environment.
                </p>
                <div className="flex space-x-4">
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                    </svg>
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                    </svg>
                  </a>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => setActivePage('home')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Home
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('about')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      About Us
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('treatments')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Treatments
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('gallery')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Gallery
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('contact')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Contact
                    </button>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Treatments</h3>
                <ul className="space-y-2">
                  <li>
                    <button 
                      onClick={() => setActivePage('treatments')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Facial Treatments
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('treatments')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Body Contouring
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('treatments')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Laser Treatments
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('treatments')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Injectables
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setActivePage('treatments')}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      Medical-Grade Skincare
                    </button>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <MapPin className="h-5 w-5 text-pink-400 mr-2 mt-0.5" />
                    <span className="text-gray-400">
                      123 Wellness Avenue<br />
                      Anytown, ST 12345
                    </span>
                  </li>
                  <li className="flex items-center">
                    <Phone className="h-5 w-5 text-pink-400 mr-2" />
                    <span className="text-gray-400">(555) 987-6543</span>
                  </li>
                  <li className="flex items-center">
                    <Mail className="h-5 w-5 text-pink-400 mr-2" />
                    <span className="text-gray-400">info@radiancespa.com</span>
                  </li>
                  <li className="flex items-center">
                    <Clock className="h-5 w-5 text-pink-400 mr-2" />
                    <span className="text-gray-400">Mon-Fri: 9am-7pm</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="border-t border-gray-800 mt-12 pt-8 text-center">
              <p className="text-gray-400 text-sm">
                © 2025 Radiance Medical Spa. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  };

  const renderAboutPage = () => {
    const team = [
      {
        name: "Dr. Sarah Williams",
        title: "Medical Director",
        image: "https://images.pexels.com/photos/5214958/pexels-photo-5214958.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
        bio: "Dr. Williams is a board-certified physician with over 15 years of experience in aesthetic medicine. She oversees all medical treatments and ensures the highest standards of safety and care."
      },
      {
        name: "Jennifer Martinez",
        title: "Lead Aesthetician",
        image: "https://images.pexels.com/photos/5407206/pexels-photo-5407206.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
        bio: "Jennifer has been a licensed aesthetician for 10 years, specializing in advanced facial treatments and chemical peels. Her personalized approach helps clients achieve their skincare goals."
      },
      {
        name: "Michael Chen",
        title: "Laser Specialist",
        image: "https://images.pexels.com/photos/5452293/pexels-photo-5452293.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
        bio: "Michael is certified in advanced laser technologies and has performed thousands of successful treatments. His expertise ensures safe and effective results for all skin types."
      },
      {
        name: "Emily Rodriguez",
        title: "Body Contouring Specialist",
        image: "https://images.pexels.com/photos/5327656/pexels-photo-5327656.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop",
        bio: "Emily specializes in non-invasive body contouring treatments. Her knowledge of anatomy and physiology helps clients achieve natural-looking results with minimal downtime."
      }
    ];

    const values = [
      {
        title: "Excellence",
        description: "We strive for excellence in all aspects of our practice, from medical care to customer service.",
        icon: <Award className="h-8 w-8 text-purple-600" />
      },
      {
        title: "Safety",
        description: "Your safety is our top priority. All treatments are performed by licensed professionals using FDA-approved technologies.",
        icon: <Shield className="h-8 w-8 text-purple-600" />
      },
      {
        title: "Personalization",
        description: "We create customized treatment plans tailored to your unique needs and goals.",
        icon: <Users className="h-8 w-8 text-purple-600" />
      },
      {
        title: "Integrity",
        description: "We provide honest recommendations and transparent pricing with no hidden fees.",
        icon: <Heart className="h-8 w-8 text-purple-600" />
      }
    ];

    return (
      <div className="w-full overflow-hidden">
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">About Radiance Medical Spa</h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                Learn about our story, our team, and our commitment to helping you look and feel your best.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
                <div className="space-y-6 text-gray-600 leading-relaxed">
                  <p>
                    Radiance Medical Spa was founded in 2015 by Dr. Sarah Williams with a vision to create a 
                    space where advanced medical aesthetics meets luxury spa experience. What began as a small 
                    practice has grown into a premier destination for those seeking the highest quality 
                    aesthetic treatments.
                  </p>
                  <p>
                    Our philosophy is simple: we believe that everyone deserves to feel confident in their own 
                    skin. We combine cutting-edge technology with a personalized approach to help our clients 
                    enhance their natural beauty and achieve their aesthetic goals.
                  </p>
                  <p>
                    Over the years, we've helped thousands of clients look and feel their best. Our commitment 
                    to excellence, safety, and results has made us a trusted name in aesthetic medicine.
                  </p>
                </div>
              </div>
              <div>
                <img 
                  src="https://images.pexels.com/photos/3997378/pexels-photo-3997378.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop" 
                  alt="Spa interior" 
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These values guide everything we do and shape how we care for our clients.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white p-8 rounded-lg shadow-md text-center">
                  <div className="mb-6 flex justify-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                      {value.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Team */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our team of licensed professionals is dedicated to providing exceptional care and results.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {team.map((member, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-md text-center">
                  <img 
                    src={member.image} 
                    alt={member.name} 
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                  />
                  <h3 className="text-xl font-semibold text-gray-900">{member.name}</h3>
                  <p className="text-purple-600 font-medium mb-4">{member.title}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Facility */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Facility</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Step into our luxurious spa designed for your comfort and relaxation.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997378/pexels-photo-3997378.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Reception area" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Reception Area</h3>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997379/pexels-photo-3997379.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Treatment room" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Treatment Room</h3>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997981/pexels-photo-3997981.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Relaxation lounge" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Relaxation Lounge</h3>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Experience the Radiance Difference</h2>
            <p className="text-xl text-purple-100 mb-8">
              Book your consultation today and take the first step toward looking and feeling your best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Book a Consultation
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderTreatmentsPage = () => {
    const treatments = [
      {
        category: "Facial Treatments",
        items: [
          {
            title: "Signature Facial",
            description: "Our customized facial addresses your specific skin concerns, including cleansing, exfoliation, extractions, mask, and hydration.",
            duration: "60 minutes",
            price: "$125"
          },
          {
            title: "HydraFacial",
            description: "Advanced treatment that cleanses, exfoliates, extracts, and hydrates using serums filled with antioxidants, peptides, and hyaluronic acid.",
            duration: "45 minutes",
            price: "$199"
          },
          {
            title: "Chemical Peel",
            description: "Improves skin texture, reduces fine lines, and addresses pigmentation issues by removing damaged outer layers of skin.",
            duration: "30 minutes",
            price: "$150-$250"
          },
          {
            title: "Microdermabrasion",
            description: "Mechanical exfoliation that removes dead skin cells to improve texture, tone, and reduce fine lines.",
            duration: "45 minutes",
            price: "$150"
          }
        ]
      },
      {
        category: "Body Contouring",
        items: [
          {
            title: "CoolSculpting",
            description: "Non-invasive fat reduction treatment that freezes and eliminates stubborn fat cells without surgery or downtime.",
            duration: "35-60 minutes per area",
            price: "$750 per area"
          },
          {
            title: "Emsculpt",
            description: "Build muscle and sculpt your body with this non-invasive procedure that induces powerful muscle contractions.",
            duration: "30 minutes",
            price: "$500 per session"
          },
          {
            title: "Cellulite Reduction",
            description: "Targeted treatment to reduce the appearance of cellulite and improve skin texture.",
            duration: "45-60 minutes",
            price: "$350 per session"
          },
          {
            title: "Body Firming Treatment",
            description: "Tightens and firms loose skin using radiofrequency technology to stimulate collagen production.",
            duration: "60 minutes",
            price: "$400 per area"
          }
        ]
      },
      {
        category: "Laser Treatments",
        items: [
          {
            title: "Laser Hair Removal",
            description: "Permanent reduction of unwanted hair using advanced laser technology safe for all skin types.",
            duration: "15-60 minutes depending on area",
            price: "$150-$500 per session"
          },
          {
            title: "IPL Photofacial",
            description: "Treats sun damage, brown spots, redness, and improves overall skin tone using intense pulsed light.",
            duration: "30 minutes",
            price: "$350 per session"
          },
          {
            title: "Laser Skin Resurfacing",
            description: "Reduces wrinkles, scars, and improves skin texture by removing damaged skin layers.",
            duration: "45-60 minutes",
            price: "$500-$1,500"
          },
          {
            title: "Vascular Laser Treatment",
            description: "Targets and reduces visible blood vessels, rosacea, and redness for clearer skin.",
            duration: "30 minutes",
            price: "$300 per session"
          }
        ]
      },
      {
        category: "Injectables",
        items: [
          {
            title: "Botox/Dysport",
            description: "Reduces the appearance of fine lines and wrinkles by relaxing facial muscles.",
            duration: "15-30 minutes",
            price: "$12-$15 per unit"
          },
          {
            title: "Dermal Fillers",
            description: "Restores volume, smooths lines, and enhances facial contours using hyaluronic acid fillers.",
            duration: "30-45 minutes",
            price: "$650-$850 per syringe"
          },
          {
            title: "Kybella",
            description: "FDA-approved injectable that reduces submental fat (double chin) without surgery.",
            duration: "30 minutes",
            price: "$600 per session"
          },
          {
            title: "Liquid Facelift",
            description: "Comprehensive treatment using a combination of fillers and Botox to rejuvenate the face without surgery.",
            duration: "60 minutes",
            price: "$1,800-$3,000"
          }
        ]
      }
    ];

    return (
      <div className="w-full overflow-hidden">
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">Our Treatments</h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                Discover our comprehensive range of advanced aesthetic treatments designed to enhance your natural beauty.
              </p>
            </div>
          </div>
        </section>

        {/* Treatments Accordion */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Treatment Menu</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore our full range of treatments and find the perfect solution for your aesthetic goals.
              </p>
            </div>
            
            <div className="space-y-6">
              {treatments.map((category, index) => (
                <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                  <button
                    className="flex justify-between items-center w-full p-6 text-left focus:outline-none"
                    onClick={() => toggleAccordion(index)}
                  >
                    <h3 className="text-2xl font-semibold text-gray-900">{category.category}</h3>
                    {activeAccordion === index ? (
                      <ChevronDown className="h-6 w-6 text-purple-600 transform rotate-180 transition-transform" />
                    ) : (
                      <ChevronDown className="h-6 w-6 text-purple-600 transition-transform" />
                    )}
                  </button>
                  
                  {activeAccordion === index && (
                    <div className="px-6 pb-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {category.items.map((treatment, treatmentIndex) => (
                          <div key={treatmentIndex} className="bg-white p-6 rounded-lg border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="text-lg font-semibold text-gray-900">{treatment.title}</h4>
                              <span className="text-purple-600 font-semibold">{treatment.price}</span>
                            </div>
                            <p className="text-gray-600 mb-4">{treatment.description}</p>
                            <div className="flex items-center text-sm text-gray-500">
                              <Clock className="h-4 w-4 mr-1" />
                              <span>{treatment.duration}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Treatment Process */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Treatment Process</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We ensure a seamless experience from consultation to results.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                  1
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Consultation</h3>
                <p className="text-gray-600">We begin with a thorough consultation to understand your concerns and goals.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                  2
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Treatment Plan</h3>
                <p className="text-gray-600">Our experts create a personalized treatment plan tailored to your needs.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                  3
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Treatment</h3>
                <p className="text-gray-600">Experience our advanced treatments in a comfortable, luxurious environment.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                  4
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Follow-Up</h3>
                <p className="text-gray-600">We monitor your progress and adjust your treatment plan as needed for optimal results.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Look?</h2>
            <p className="text-xl text-purple-100 mb-8">
              Book your consultation today and discover which treatments are right for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Book a Consultation
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderGalleryPage = () => {
    const galleryTabs = [
      { id: 'facial', label: 'Facial Treatments' },
      { id: 'body', label: 'Body Contouring' },
      { id: 'laser', label: 'Laser Treatments' },
      { id: 'injectables', label: 'Injectables' }
    ];

    const galleryImages = {
      facial: [
        {
          before: "https://images.pexels.com/photos/3762892/pexels-photo-3762892.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/3762893/pexels-photo-3762893.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "HydraFacial",
          description: "Results after 3 sessions of HydraFacial treatment, showing improved skin texture and hydration."
        },
        {
          before: "https://images.pexels.com/photos/3764013/pexels-photo-3764013.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/3764119/pexels-photo-3764119.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "Chemical Peel",
          description: "Results after a series of chemical peels, showing reduction in hyperpigmentation and improved skin tone."
        },
        {
          before: "https://images.pexels.com/photos/3764168/pexels-photo-3764168.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/3764169/pexels-photo-3764169.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "Microneedling",
          description: "Results after 4 sessions of microneedling, showing improvement in acne scars and skin texture."
        }
      ],
      body: [
        {
          before: "https://images.pexels.com/photos/7446144/pexels-photo-7446144.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/7446145/pexels-photo-7446145.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "CoolSculpting",
          description: "Results after 2 CoolSculpting sessions, showing reduction in abdominal fat."
        },
        {
          before: "https://images.pexels.com/photos/7446146/pexels-photo-7446146.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/7446147/pexels-photo-7446147.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "Emsculpt",
          description: "Results after 4 Emsculpt sessions, showing increased muscle definition and fat reduction."
        }
      ],
      laser: [
        {
          before: "https://images.pexels.com/photos/7446989/pexels-photo-7446989.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/7446990/pexels-photo-7446990.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "Laser Hair Removal",
          description: "Results after 6 sessions of laser hair removal, showing significant reduction in hair growth."
        },
        {
          before: "https://images.pexels.com/photos/7446991/pexels-photo-7446991.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/7446992/pexels-photo-7446992.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "IPL Photofacial",
          description: "Results after 3 IPL treatments, showing reduction in sun damage and improved skin tone."
        }
      ],
      injectables: [
        {
          before: "https://images.pexels.com/photos/7446997/pexels-photo-7446997.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/7446998/pexels-photo-7446998.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "Botox",
          description: "Results 2 weeks after Botox treatment, showing reduction in forehead lines and crow's feet."
        },
        {
          before: "https://images.pexels.com/photos/7446999/pexels-photo-7446999.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          after: "https://images.pexels.com/photos/7447000/pexels-photo-7447000.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop",
          treatment: "Dermal Fillers",
          description: "Results immediately after dermal filler treatment, showing enhanced lip volume and reduced nasolabial folds."
        }
      ]
    };

    return (
      <div className="w-full overflow-hidden">
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">Before & After Gallery</h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                See real results from our treatments and discover what's possible for you.
              </p>
            </div>
          </div>
        </section>

        {/* Gallery Tabs */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <div className="flex flex-wrap justify-center gap-4">
                {galleryTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveGalleryTab(tab.id)}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      activeGalleryTab === tab.id
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-16">
              {galleryImages[activeGalleryTab as keyof typeof galleryImages].map((item, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-lg shadow-md">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">{item.treatment}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <div className="relative">
                        <img 
                          src={item.before} 
                          alt={`Before ${item.treatment}`} 
                          className="w-full h-80 object-cover rounded-lg"
                        />
                        <div className="absolute top-4 left-4 bg-gray-900/70 text-white px-4 py-1 rounded-full text-sm font-medium">
                          Before
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="relative">
                        <img 
                          src={item.after} 
                          alt={`After ${item.treatment}`} 
                          className="w-full h-80 object-cover rounded-lg"
                        />
                        <div className="absolute top-4 left-4 bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                          After
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 mt-6 text-center">{item.description}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-16 text-center">
              <p className="text-gray-600 mb-8">
                Results may vary from person to person. Schedule a consultation to discuss your specific goals and expected results.
              </p>
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Book a Consultation
              </button>
            </div>
          </div>
        </section>

        {/* Facility Gallery */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Facility</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Take a virtual tour of our luxurious medical spa.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997378/pexels-photo-3997378.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Reception area" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Reception Area</h3>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997379/pexels-photo-3997379.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Treatment room" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Treatment Room</h3>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997981/pexels-photo-3997981.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Relaxation lounge" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Relaxation Lounge</h3>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997380/pexels-photo-3997380.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Laser treatment room" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Laser Treatment Room</h3>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997382/pexels-photo-3997382.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Skincare retail area" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Skincare Retail Area</h3>
                </div>
              </div>
              
              <div className="rounded-lg overflow-hidden shadow-md">
                <img 
                  src="https://images.pexels.com/photos/3997383/pexels-photo-3997383.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop" 
                  alt="Consultation room" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-white">
                  <h3 className="font-semibold text-gray-900">Consultation Room</h3>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Experience the Radiance Difference?</h2>
            <p className="text-xl text-purple-100 mb-8">
              Book your consultation today and take the first step toward looking and feeling your best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Book a Consultation
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                Contact Us
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderMembershipPage = () => {
    const membershipPlans = [
      {
        title: "Silver",
        price: "$99",
        features: [
          "One signature treatment monthly",
          "10% off additional services",
          "5% off retail products",
          "Member-only events",
          "Rollover one unused treatment per quarter"
        ],
        popular: false
      },
      {
        title: "Gold",
        price: "$179",
        features: [
          "Two signature treatments monthly",
          "15% off additional services",
          "10% off retail products",
          "Priority booking",
          "Complimentary birthday treatment",
          "Rollover one unused treatment per month",
          "Quarterly skin analysis"
        ],
        popular: true
      },
      {
        title: "Platinum",
        price: "$299",
        features: [
          "Four signature treatments monthly",
          "20% off additional services",
          "15% off retail products",
          "VIP priority booking",
          "Complimentary guest pass quarterly",
          "Unlimited treatment rollovers",
          "Monthly skin analysis",
          "Personalized skincare regimen",
          "Complimentary product samples"
        ],
        popular: false
      }
    ];

    const faqs = [
      {
        question: "How does the membership work?",
        answer: "Our membership program is a monthly subscription that gives you access to regular treatments at a discounted rate. You pay a fixed monthly fee and receive a set number of treatments each month, along with additional benefits like discounts on services and products."
      },
      {
        question: "Can I share my membership with family members?",
        answer: "Memberships are non-transferable and can only be used by the member. However, we offer family plans that provide discounts when multiple family members join."
      },
      {
        question: "What if I don't use all my treatments in a month?",
        answer: "Depending on your membership level, you can roll over unused treatments for a specified period. Silver members can roll over one treatment per quarter, Gold members can roll over one treatment per month, and Platinum members have unlimited rollovers."
      },
      {
        question: "Is there a commitment period?",
        answer: "Our memberships require a 3-month minimum commitment. After that, you can cancel with 30 days' notice."
      },
      {
        question: "Can I upgrade or downgrade my membership?",
        answer: "Yes, you can upgrade your membership at any time. Downgrades can be made after your initial 3-month commitment period with 30 days' notice."
      }
    ];

    return (
      <div className="w-full overflow-hidden">
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">Membership Program</h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                Join our exclusive membership program and enjoy regular treatments, special discounts, and VIP benefits.
              </p>
            </div>
          </div>
        </section>

        {/* Membership Plans */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Membership Plan</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Select the plan that best fits your beauty and wellness goals.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {membershipPlans.map((plan, index) => (
                <div 
                  key={index} 
                  className={`bg-white rounded-lg shadow-lg overflow-hidden border ${
                    plan.popular ? 'border-purple-500 transform scale-105' : 'border-gray-200'
                  }`}
                >
                  {plan.popular && (
                    <div className="bg-purple-500 text-white text-center py-2">
                      <span className="text-sm font-bold uppercase">Most Popular</span>
                    </div>
                  )}
                  
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.title}</h3>
                    <div className="text-4xl font-bold text-purple-600 mb-6">
                      {plan.price}<span className="text-lg font-normal text-gray-600">/month</span>
                    </div>
                    
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button 
                      onClick={() => setActivePage('booking')}
                      className={`w-full py-3 rounded-lg font-medium transition-colors ${
                        plan.popular
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      Join Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership Benefits */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Membership Benefits</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Enjoy these exclusive benefits when you join our membership program.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <Sparkles className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Regular Treatments</h3>
                <p className="text-gray-600">
                  Enjoy regular treatments at a significantly discounted rate compared to pay-as-you-go pricing.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <DollarSign className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Exclusive Discounts</h3>
                <p className="text-gray-600">
                  Receive special discounts on additional services and retail products not available to non-members.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <Calendar className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Priority Booking</h3>
                <p className="text-gray-600">
                  Members get priority access to appointment times, including peak hours and popular providers.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Member-Only Events</h3>
                <p className="text-gray-600">
                  Access to exclusive events, product launches, and educational seminars only available to members.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <Gift className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Special Perks</h3>
                <p className="text-gray-600">
                  Enjoy birthday treatments, complimentary upgrades, and special seasonal offers throughout the year.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <Repeat className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Rollover Treatments</h3>
                <p className="text-gray-600">
                  Flexibility to roll over unused treatments based on your membership level, so you never lose your benefits.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Find answers to common questions about our membership program.
              </p>
            </div>
            
            <div className="max-w-3xl mx-auto">
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                    <button
                      className="flex justify-between items-center w-full p-6 text-left focus:outline-none"
                      onClick={() => toggleAccordion(index)}
                    >
                      <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                      {activeAccordion === index ? (
                        <ChevronDown className="h-5 w-5 text-purple-600 transform rotate-180 transition-transform" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-purple-600 transition-transform" />
                      )}
                    </button>
                    
                    {activeAccordion === index && (
                      <div className="px-6 pb-6">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Join?</h2>
            <p className="text-xl text-purple-100 mb-8">
              Become a member today and start enjoying exclusive benefits and savings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Join Now
              </button>
              <button 
                onClick={() => setActivePage('contact')}
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors"
              >
                Contact for Details
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderContactPage = () => {
    return (
      <div className="w-full overflow-hidden">
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">Contact Us</h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                We're here to answer your questions and help you schedule your visit.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="bg-gray-50 p-8 rounded-lg shadow-md">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Spa Information</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <MapPin className="h-6 w-6 text-purple-600 mr-4 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                        <p className="text-gray-600">123 Wellness Avenue</p>
                        <p className="text-gray-600">Anytown, ST 12345</p>
                        <a 
                          href="https://maps.google.com" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-purple-600 text-sm hover:text-purple-700 transition-colors mt-1 inline-block"
                        >
                          Get Directions
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Phone className="h-6 w-6 text-purple-600 mr-4 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                        <p className="text-gray-600">(555) 987-6543</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Mail className="h-6 w-6 text-purple-600 mr-4 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                        <p className="text-gray-600">info@radiancespa.com</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <Clock className="h-6 w-6 text-purple-600 mr-4 mt-1" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Spa Hours</h3>
                        <div className="grid grid-cols-2 gap-2 text-gray-600">
                          <div>Monday - Friday</div>
                          <div>9:00 AM - 7:00 PM</div>
                          <div>Saturday</div>
                          <div>9:00 AM - 5:00 PM</div>
                          <div>Sunday</div>
                          <div>Closed</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <h3 className="font-semibold text-gray-900 mb-4">Connect With Us</h3>
                    <div className="flex space-x-4">
                      <a href="#" className="bg-gray-200 hover:bg-gray-300 transition-colors p-2 rounded-full">
                        <svg className="h-6 w-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                        </svg>
                      </a>
                      <a href="#" className="bg-gray-200 hover:bg-gray-300 transition-colors p-2 rounded-full">
                        <svg className="h-6 w-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                  <form className="space-y-6">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                        placeholder="Your name"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                          placeholder="Your email"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="contact-phone"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                          placeholder="Your phone number"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <select
                        id="subject"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                      >
                        <option value="">Select a subject</option>
                        <option value="appointment">Appointment Inquiry</option>
                        <option value="treatment">Treatment Information</option>
                        <option value="membership">Membership Questions</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                        placeholder="Your message"
                      ></textarea>
                    </div>
                    
                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          I consent to having this website store my submitted information so they can respond to my inquiry.
                        </span>
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                    >
                      Send Message
                    </button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      We'll get back to you within 24 hours.
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-12 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gray-200 h-96 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Our Location</h3>
                <p className="text-gray-600">123 Wellness Avenue, Anytown, ST 12345</p>
                <p className="text-sm text-gray-500 mt-2">Interactive map will be integrated here</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Experience the Radiance Difference?</h2>
            <p className="text-xl text-purple-100 mb-8">
              Book your consultation today and take the first step toward looking and feeling your best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setActivePage('booking')}
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Book a Consultation
              </button>
              <a 
                href="tel:5559876543" 
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors inline-flex items-center justify-center"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call (555) 987-6543
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderBookingPage = () => {
    const treatmentTypes = [
      { value: "facial", label: "Facial Treatment" },
      { value: "body", label: "Body Contouring" },
      { value: "laser", label: "Laser Treatment" },
      { value: "injectable", label: "Injectable" },
      { value: "consultation", label: "Consultation" }
    ];

    const practitioners = [
      { value: "dr-williams", label: "Dr. Sarah Williams" },
      { value: "jennifer", label: "Jennifer Martinez" },
      { value: "michael", label: "Michael Chen" },
      { value: "emily", label: "Emily Rodriguez" },
      { value: "no-preference", label: "No Preference" }
    ];

    return (
      <div className="w-full overflow-hidden">
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">Book Your Appointment</h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                Schedule your visit online or call us at (555) 987-6543.
              </p>
            </div>
          </div>
        </section>

        {/* Booking Form */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Request an Appointment</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Complete the form below to request an appointment. Our staff will contact you to confirm 
                  your appointment time. For same-day appointments, please call us directly.
                </p>
                
                <div className="bg-purple-50 p-6 rounded-lg border border-purple-200 mb-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Before Your Visit:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0" />
                      Complete new client forms if this is your first visit
                    </li>
                    <li className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0" />
                      Arrive 15 minutes early for your appointment
                    </li>
                    <li className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0" />
                      Avoid wearing makeup for facial treatments
                    </li>
                    <li className="flex items-center text-gray-600">
                      <CheckCircle className="h-5 w-5 text-purple-600 mr-3 flex-shrink-0" />
                      Avoid sun exposure before laser treatments
                    </li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="flex items-center mb-4">
                    <Phone className="h-6 w-6 text-purple-600 mr-3" />
                    <h3 className="text-lg font-semibold text-gray-900">Need a same-day appointment?</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    For urgent appointments or same-day bookings, please call us directly at:
                  </p>
                  <a 
                    href="tel:5559876543" 
                    className="text-xl font-semibold text-purple-600 hover:text-purple-700 transition-colors"
                  >
                    (555) 987-6543
                  </a>
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Appointment Request Form</h3>
                <form className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                        placeholder="Your email"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="treatment-type" className="block text-sm font-medium text-gray-700 mb-1">
                      Treatment Type
                    </label>
                    <select
                      id="treatment-type"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                    >
                      <option value="">Select treatment type</option>
                      {treatmentTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="practitioner" className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Practitioner
                    </label>
                    <select
                      id="practitioner"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                    >
                      <option value="">Select practitioner</option>
                      {practitioners.map((practitioner) => (
                        <option key={practitioner.value} value={practitioner.value}>
                          {practitioner.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        id="date"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Time
                      </label>
                      <select
                        id="time"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                      >
                        <option value="">Select a time</option>
                        <option value="morning">Morning (9am-12pm)</option>
                        <option value="afternoon">Afternoon (12pm-4pm)</option>
                        <option value="evening">Evening (4pm-7pm)</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                      Special Requests or Notes
                    </label>
                    <textarea
                      id="notes"
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                      placeholder="Any special requests or concerns"
                    ></textarea>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">I'm a new client</span>
                    </label>
                  </div>
                  
                  <button
                    type="submit"
                    className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                  >
                    Request Appointment
                  </button>
                  
                  <p className="text-xs text-gray-500 text-center">
                    This form is for appointment requests only. We'll contact you to confirm your appointment time.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">What to Expect</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                We want your visit to be as smooth and comfortable as possible. Here's what to expect when you visit our spa.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-purple-600 font-bold text-xl">1</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Before Your Visit</h3>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Complete new client forms (if applicable)</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Follow pre-treatment instructions</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Arrive 15 minutes early</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Bring any questions you have</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-purple-600 font-bold text-xl">2</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">During Your Visit</h3>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Check in with our front desk</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Enjoy our relaxation lounge</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Consultation with your provider</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Treatment in our luxury spa rooms</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <span className="text-purple-600 font-bold text-xl">3</span>
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">After Your Visit</h3>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Receive post-treatment instructions</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Schedule your next appointment</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Browse our skincare products</span>
                  </li>
                  <li className="flex items-start text-gray-600">
                    <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Follow up with any questions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cancellation Policy */}
        <section className="py-12 bg-white border-t border-gray-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cancellation Policy</h3>
              <p className="text-gray-600 mb-4">
                We understand that schedules change. However, to best serve all our clients, we kindly request 
                at least 24 hours' notice for cancellations or rescheduling.
              </p>
              <p className="text-gray-600">
                Appointments cancelled with less than 24 hours' notice may be subject to a cancellation fee 
                of 50% of the scheduled service. No-shows will be charged the full service amount.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-purple-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Experience the Radiance Difference?</h2>
            <p className="text-xl text-purple-100 mb-8">
              Book your appointment today and take the first step toward looking and feeling your best.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:5559876543" 
                className="bg-white text-purple-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call (555) 987-6543
              </a>
              <button 
                onClick={() => setActivePage('contact')}
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors inline-flex items-center justify-center"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Send a Message
              </button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  return (
    <div>
      {renderNavigation()}
      {renderPage()}
    </div>
  );
};

// These are needed for the component to work
const DollarSign = (props: any) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" x2="12" y1="2" y2="22"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const Gift = (props: any) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="8" width="18" height="4" rx="1"></rect>
    <path d="M12 8v13"></path>
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path>
    <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"></path>
  </svg>
);

const Repeat = (props: any) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m17 2 4 4-4 4"></path>
    <path d="M3 11v-1a4 4 0 0 1 4-4h14"></path>
    <path d="m7 22-4-4 4-4"></path>
    <path d="M21 13v1a4 4 0 0 1-4 4H3"></path>
  </svg>
);